import Service from 'react-reservice';

import { IPriceAlert, IPriceAlertConfig } from '@aqx-core/common/api/price-alert';
import {
  EPriceAlertTab,
  MAX_ALERTS_FOR_ALL_SYMBOLS,
  MAX_ALERTS_PER_SYMBOL
} from '@aqx-core/common/constants/price-alert';

import {
  ILivePricesMap,
  IPriceAlertSymbolDetailMap,
  ISymbolDisabledMap,
  ISymbolExpandedMap,
  ISymbolPriceAlertsMap
} from './types';

interface IPriceAlertServiceContext {
  currentSymbol?: string;

  priceAlertsMap: ISymbolPriceAlertsMap;
  livePricesMap: ILivePricesMap;
  symbolDetailsMap: IPriceAlertSymbolDetailMap;
  symbolDisabledMap: ISymbolDisabledMap;
  // For desktop only to determine whether price alert accordion stays open
  symbolExpandedMap: ISymbolExpandedMap;

  priceAlertsLoading: boolean;
  alertConfigLoading: boolean;
  submitting: boolean;

  showBulkEdit: boolean;
  selectedAlerts: string[];

  activeTab: EPriceAlertTab;
  // For desktop only to determine whether or not Alert Form is shown
  showAlertForm: boolean;

  editAlertId?: string;

  alertConfig: IPriceAlertConfig;

  // For desktop only to reset list heights on bulk delete
  onDeleteResetList: boolean;
}

const defaultContext: IPriceAlertServiceContext = {
  currentSymbol: undefined,

  priceAlertsMap: {},
  livePricesMap: {},
  symbolDetailsMap: {},
  symbolDisabledMap: {},
  symbolExpandedMap: {},

  priceAlertsLoading: false,
  alertConfigLoading: false,
  submitting: false,

  showBulkEdit: false,
  selectedAlerts: [],

  activeTab: EPriceAlertTab.CURRENT_SYMBOL,
  showAlertForm: false,

  alertConfig: {
    symbolLimit: MAX_ALERTS_PER_SYMBOL,
    accountLimit: MAX_ALERTS_FOR_ALL_SYMBOLS
  },

  onDeleteResetList: false
};

class PriceAlertService extends Service<IPriceAlertServiceContext> {
  constructor() {
    super();

    this.context = defaultContext;
  }

  clearCurrentSymbol = () => {
    this.context.currentSymbol = undefined;
  };

  setCurrentSymbol = (symbol: string) => {
    this.context.currentSymbol = symbol;
  };

  clearPriceAlertsMap = () => {
    this.context.priceAlertsMap = {};
  };

  setPriceAlertsMap = (priceAlertsMap: ISymbolPriceAlertsMap) => {
    this.context.priceAlertsMap = priceAlertsMap;
  };

  updatePriceAlert = (newPriceAlert: IPriceAlert) => {
    const { symbol } = newPriceAlert;
    if (!this.context.priceAlertsMap[symbol]) {
      return;
    }

    const existingAlert = this.context.priceAlertsMap[symbol].alerts[newPriceAlert.alertId];

    const newSymbolPriceAlertObj = {
      ...this.context.priceAlertsMap[symbol],
      alerts: {
        ...this.context.priceAlertsMap[symbol].alerts,
        [newPriceAlert.alertId]: {
          ...existingAlert,
          ...newPriceAlert
        }
      }
    };
    this.context.priceAlertsMap = {
      ...this.context.priceAlertsMap,
      [symbol]: newSymbolPriceAlertObj
    };
  };

  updateAlertToggle = (alertId: string, symbol: string, toggle: boolean) => {
    const currentMap = this.context.priceAlertsMap;
    const symbolAlerts = currentMap[symbol];
    if (!symbolAlerts || !symbolAlerts.alerts[alertId]) {
      return;
    }
    this.setPriceAlertsMap({
      ...currentMap,
      [symbol]: {
        ...symbolAlerts,
        alerts: {
          ...symbolAlerts.alerts,
          [alertId]: {
            ...symbolAlerts.alerts[alertId],
            toggle
          }
        }
      }
    });
  };

  clearLivePricesMap = () => {
    this.context.livePricesMap = {};
  };

  updateLivePricesMap = (incomingLivePricesMap: ILivePricesMap) => {
    this.context.livePricesMap = {
      ...this.context.livePricesMap,
      ...incomingLivePricesMap
    };
  };

  clearSymbolDisabledMap = () => {
    this.context.symbolDisabledMap = {};
  };

  updateSymbolDisabledMap = (incomingSymbolDisabledMap: ISymbolDisabledMap) => {
    this.context.symbolDisabledMap = {
      ...this.context.symbolDisabledMap,
      ...incomingSymbolDisabledMap
    };
  };

  clearSymbolExpandedMap = () => {
    this.context.symbolExpandedMap = {};
  };

  updateSymbolExpandedMap = (incomingSymbolExpandedMap: ISymbolExpandedMap) => {
    this.context.symbolExpandedMap = {
      ...this.context.symbolExpandedMap,
      ...incomingSymbolExpandedMap
    };
  };

  updateBidPriceOnLivePrices = (bid: number, symbol: string) => {
    if (!this.context.livePricesMap[symbol]) {
      return;
    }

    this.context.livePricesMap = {
      ...this.context.livePricesMap,

      [symbol]: {
        ...this.context.livePricesMap[symbol],
        bid
      }
    };
  };

  clearSymbolDetailsMap = () => {
    this.context.symbolDetailsMap = {};
  };

  updateSymbolDetailsMap = (incomingSymbolDetailsMap: IPriceAlertSymbolDetailMap) => {
    this.context.symbolDetailsMap = {
      ...this.context.symbolDetailsMap,
      ...incomingSymbolDetailsMap
    };
  };

  setPriceAlertsLoading = (loading: boolean) => {
    this.context.priceAlertsLoading = loading;
  };

  setAlertConfigLoading = (loading: boolean) => {
    this.context.alertConfigLoading = loading;
  };

  setSubmitting = (submitting: boolean) => {
    this.context.submitting = submitting;
  };

  setShowBulkEdit = (showBulkEdit: boolean) => {
    this.context.showBulkEdit = showBulkEdit;
  };

  addAlertIdsToSelected = (ids: string[]) => {
    const newSelectedAlerts = new Set([...this.context.selectedAlerts]);
    ids.forEach((id: string) => {
      if (!newSelectedAlerts.has(id)) {
        newSelectedAlerts.add(id);
      }
    });
    this.context.selectedAlerts = Array.from(newSelectedAlerts);
  };

  removeAlertIdsFromSelected = (ids: string[]) => {
    const newSelectedAlerts = new Set([...this.context.selectedAlerts]);
    ids.forEach((id: string) => {
      if (newSelectedAlerts.has(id)) {
        newSelectedAlerts.delete(id);
      }
    });
    this.context.selectedAlerts = Array.from(newSelectedAlerts);
  };

  clearAllSelectedAlerts = () => {
    this.context.selectedAlerts = [];
  };

  setAllSelectedAlerts = (alertIds: string[]) => {
    this.context.selectedAlerts = alertIds;
  };

  clearEditAlertId = () => {
    this.context.editAlertId = undefined;
  };

  setEditAlertId = (alertId: string) => {
    this.context.editAlertId = alertId;
  };

  removePriceAlerts = (alertIds: string[]) => {
    const newMap = { ...this.context.priceAlertsMap };

    for (const symbol of Object.keys(newMap)) {
      const symbolAlerts = newMap[symbol];
      const newAlerts = { ...symbolAlerts.alerts };
      let changed = false;

      for (const id of alertIds) {
        if (newAlerts[id]) {
          delete newAlerts[id];
          changed = true;
        }
      }

      if (changed) {
        if (Object.keys(newAlerts).length === 0) {
          delete newMap[symbol];
        } else {
          newMap[symbol] = { ...symbolAlerts, alerts: newAlerts };
        }
      }
    }

    this.context.priceAlertsMap = newMap;
  };

  setActiveTab = (tab: EPriceAlertTab) => {
    this.context.activeTab = tab;
  };

  setShowAlertForm = (showAlertForm: boolean) => {
    this.context.showAlertForm = showAlertForm;
  };

  setAlertConfig = (config: IPriceAlertConfig) => {
    this.context.alertConfig = config;
  };

  setOnDeleteResetList = (onDeleteResetList: boolean) => {
    this.context.onDeleteResetList = onDeleteResetList;
  };

  // Use for unit testing only
  resetContext = () => {
    this.context = { ...defaultContext };
  };
}

export const priceAlertService = new PriceAlertService();
